import { DEFAULT_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Button, Container, Dropdown, Stack, Form, Card, Breadcrumb } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './AlarmSettingsScreen.module.scss';
import { AlarmSettingSearchCriteria } from 'api/alarmSettings/models/AlarmSettingSearchCriteria';
import { Paged } from 'api/common/types/Page';
import { AlarmSettingDto } from 'api/alarmSettings/models/AlarmSettingDto';
import AlarmSettingsService from 'api/alarmSettings/AlarmSettingsService';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import Loading from 'common/services/Loading';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';

type Props = {
};

const AlarmSettingsScreen: React.FC<Props> = () => {
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [criteria, setCriteria] = useState<AlarmSettingSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderBy: 'asc',
        orderColumn: 'st.name'
    });
    const [alarmSettingsPage, setAlarmSettingsPage] = useState<Paged<AlarmSettingDto>>();
    const [filters, setFilters] = useState({
        allIn: ''
    });
    const hasAlarmSettingsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['ALARM_SETTINGS_WRITE']);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const getData = async () => {
        try {
            Loading.show();
            const page = await AlarmSettingsService.getList(criteria);
            setAlarmSettingsPage(page);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get alarm settings', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const renderTableActionCell = (row: AlarmSettingDto) => {
        return <Dropdown>
            <Dropdown.Toggle id="dropdown-basic-button" size="sm" variant="outline-secondary">...</Dropdown.Toggle>
            <Dropdown.Menu popperConfig={{ strategy: 'fixed' }} renderOnMount>
                <Dropdown.Item onClick={() => navigate(`/alarmsetting/details/${row.id}`)}>{t('common.details')}</Dropdown.Item>
                { hasAlarmSettingsWritePolicy && <>
                <Dropdown.Item onClick={() => navigate(`/alarmsetting/edit/${row.id}`)}>{t('common.edit')}</Dropdown.Item>
                <Dropdown.Item onClick={() => showRemoveItemDialog(row)}>{t('common.delete')}</Dropdown.Item>
                </>
                }
            </Dropdown.Menu>
        </Dropdown>
    };

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: (isFilterAsc ? 'asc' : 'desc'), orderColumn: field });
    }

    const tableColumns: ListingTableColumn<AlarmSettingDto>[] = [
        {
            field: 'name',
            name: t('alarm_settings.list.name') ?? '',
            onSearch: onTableFilter,
            searchField: 'a.name'
        },
        {
            name: '',
            width: '60px',
            cellAlignment: 'right',
            preventClick: true,
            renderCell: renderTableActionCell,
            cellStyle: { overflow: 'unset' }
        },
    ];

    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<AlarmSettingDto | null>(null);

    const showRemoveItemDialog = async (item: AlarmSettingDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onRowClick = (event: any, id?: string) => {
        if (id) {
            if (event.ctrlKey) {
                window.open(location.pathname + '/details/' + id, '_blank');
                return;
            }
            navigate(`/alarmsetting/details/${id}`);
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await AlarmSettingsService.remove(itemToRemove);
            onCancelRemove();
            toast.success(t('messages.record_delete_success'));
            setCriteria({ ...criteria, page: 1 })
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete api_tokens', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    useEffect(() => {
        getData();
    }, [criteria]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFilters({ ...filters, [key]: value });
    }
    function clearFilters() {
        setFilters({ allIn: '' });
        setCriteria({ ...criteria, page: 1, allIn: '' });
    }
    const onSearch = () => {
        setCriteria({ ...criteria, allIn: filters.allIn });
    }

    return (
        <ScreenTitle title={t('alarm_settings.title')}>
                <Breadcrumb>
                    <Breadcrumb.Item active>{t('alarm_settings.title')}</Breadcrumb.Item>
                </Breadcrumb>
                <Container fluid>
                <Card className={styles.filters}>
                    <Card.Body>
                        <Stack direction="horizontal" gap={3} >
                            <Form.Control type="text" value={filters.allIn} className="me-auto" name="allIn" onChange={handleChange} onKeyDown={(e: any) => { if (e.key === 'Enter') onSearch(); }} placeholder={t('common.write_here')} />
                            <Button variant="secondary" onClick={onSearch}>{t('common.search')}</Button>
                            <div className="vr" />
                            <Button variant="outline-danger" onClick={clearFilters}>{t('common.clear')}</Button>
                            <Button variant="primary" onClick={() => navigate('/alarmsetting/new/')}>{t('common.new')}</Button>
                        </Stack>
                    </Card.Body>
                </Card>
            </Container>
            <Container fluid>
                <ListingTable
                    columns={tableColumns}
                    rows={alarmSettingsPage?.items || []}
                    onRowClick={(row, _, event) => onRowClick(event, row.id)}
                    allowHover={true}
                    initialSearch={{ colField: 'st.name', isOrderAsc: true }}
                />
                <PaginationWithInfo
                    itemName={t('alarm_settings.title')}
                    currentPage={alarmSettingsPage?.currentPage ?? 1}
                    pageItems={alarmSettingsPage?.items.length || 0}
                    totalItems={alarmSettingsPage?.totalItems || 0}
                    onChange={page => setCriteria({ ...criteria, page: page })}
                />
            </Container>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: itemToRemove?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default AlarmSettingsScreen;
